import * as React from "react";
import LIcon from "../LIcon";
import LPressable from "../LPressable";
import PremiumButton from "../PremiumButton";
import { useDownloads } from "@utils/downloadManager/hooks/useDownloads";
import { useData } from "../../runtime/provider";
import { useDownloadProgressDetails } from "@utils/downloadManager/hooks";
import { memo } from "react";
import { Platform } from "react-native";
import Svg, { Circle } from "react-native-svg";
import { ICON_SIZE } from "@components/Collection/constants";
import { theme } from "@theme";
import Animated, {
  useAnimatedProps,
  useSharedValue,
  withSequence,
  withTiming,
} from "react-native-reanimated";
import { useOfflineAccess } from "@utils/offlineAccess/useOfflineAccess";
import useInternetStatus from "@utils/internetStatus";
import { showOfflineAccessPrompt } from "@utils/offlineAccess/prompt";
import { useMe } from "@hooks/trpc/useMe";
import { captureException } from "@utils/captureException";

const AnimatedCircle = Animated.createAnimatedComponent(Circle);
const ENABLE_FAKE_DOWNLOAD_PROGRESS = true;
const FAKE_CURRENT_TRACK_FILL = 0.86;
const FAKE_PROGRESS_DURATION_MS = 4500;
const REAL_PROGRESS_DURATION_MS = 220;

const clampProgress = (progress: number) => Math.max(0, Math.min(1, progress));

const getVisualDownloadProgress = ({
  downloadedTracks,
  progress,
  totalTracks,
}: {
  downloadedTracks: number;
  progress: number;
  totalTracks: number;
}) => {
  const realProgress = clampProgress(progress);

  if (!ENABLE_FAKE_DOWNLOAD_PROGRESS || totalTracks <= 0 || realProgress >= 1) {
    return realProgress;
  }

  const nextTrackProgress =
    (Math.min(downloadedTracks + 1, totalTracks) / totalTracks) * 0.999;

  return Math.max(
    realProgress,
    realProgress + (nextTrackProgress - realProgress) * FAKE_CURRENT_TRACK_FILL,
  );
};

const DownloadProgressCircle = memo(
  ({
    downloadedTracks,
    progress,
    totalTracks,
  }: {
    downloadedTracks: number;
    progress: number;
    totalTracks: number;
  }) => {
    const size = 22;
    const strokeWidth = 2.5;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const realProgress = clampProgress(progress);
    const visualProgress = getVisualDownloadProgress({
      downloadedTracks,
      progress,
      totalTracks,
    });
    const realDisplayedProgress = 1 - realProgress;
    const visualDisplayedProgress = 1 - visualProgress;
    const animatedProgress = useSharedValue(realDisplayedProgress);

    React.useEffect(() => {
      if (visualProgress === realProgress) {
        animatedProgress.value = withTiming(realDisplayedProgress, {
          duration: REAL_PROGRESS_DURATION_MS,
        });
        return;
      }

      animatedProgress.value = withSequence(
        withTiming(realDisplayedProgress, {
          duration: REAL_PROGRESS_DURATION_MS,
        }),
        withTiming(visualDisplayedProgress, {
          duration: FAKE_PROGRESS_DURATION_MS,
        }),
      );
    }, [
      animatedProgress,
      realDisplayedProgress,
      realProgress,
      visualDisplayedProgress,
      visualProgress,
    ]);

    const animatedProps = useAnimatedProps(() => ({
      strokeDashoffset: circumference * (1 - animatedProgress.value),
    }));

    return (
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={theme.colors.subTextGrey}
          // strokeOpacity={0.35}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <AnimatedCircle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={theme.colors.buttonTextPrimary}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${circumference} ${circumference}`}
          animatedProps={animatedProps}
          fill="none"
          transform={`translate(${size} 0) scale(-1 1) rotate(-90 ${size / 2} ${size / 2})`}
        />
      </Svg>
    );
  },
);
DownloadProgressCircle.displayName = "DownloadProgressCircle";

const DownloadButton = ({
  id,
  type,
  liked,
  toggleCollectionLike,
}: {
  id: string;
  type: "album" | "playlist";
  liked: boolean;
  toggleCollectionLike: (id: string, type: "album" | "playlist") => void;
}) => {
  const downloadManager = useDownloads();
  const { offlineAccess } = useData();
  const downloadProgress = useDownloadProgressDetails(id);
  const { isValid: hasOfflineAccess } = useOfflineAccess();
  const { isPremium } = useMe();
  const isOnline = useInternetStatus();
  const downloadStatus = downloadProgress.status;

  const deleteCollection = React.useCallback(() => {
    try {
      downloadManager.cancelOrDeleteDownloads(id).catch(console.error);
    } catch (error) {
      console.error("Failed to delete collection:", error);
    }
  }, [downloadManager, id]);

  const promptForOfflineAccess = React.useCallback(() => {
    showOfflineAccessPrompt(offlineAccess.observeOfflineAccessState().reason);
  }, [offlineAccess]);

  const downloadCollection = React.useCallback(async () => {
    if (!isOnline) {
      const { isValid, reason } = offlineAccess.observeOfflineAccessState();
      if (!isValid) {
        showOfflineAccessPrompt(reason);
        return;
      }
    }

    // A premium user can land here with no valid local lease (the lease
    // refresh chain failed silently at startup). Heal it in the background
    // so offline playback of the download works; the server independently
    // authorizes the download itself.
    if (!hasOfflineAccess && isOnline) {
      offlineAccess.refreshOfflineAccess().catch((error) =>
        captureException(error, {
          channel: "error",
          area: "downloads",
          operation: "refresh-offline-access-on-download",
        }),
      );
    }

    try {
      await downloadManager.downloadCollection(id, type);
      if (!liked) {
        toggleCollectionLike(id, type);
      }
    } catch (error) {
      console.error("Failed to download collection:", error);
    }
  }, [
    downloadManager,
    offlineAccess,
    id,
    isOnline,
    hasOfflineAccess,
    type,
    liked,
    toggleCollectionLike,
  ]);

  if (Platform.OS === "web") {
    return null;
  }

  if (downloadStatus === "downloading") {
    return (
      <LPressable
        sentryEventName={`delete_collection_${id}`}
        accessibilityLabel={`${type} download in progress`}
        accessibilityRole="button"
        testID={`collection-download-button-${id}`}
        onPress={() => {
          deleteCollection();
        }}
        hitSlop={12}
      >
        <DownloadProgressCircle
          downloadedTracks={downloadProgress.downloadedTracks}
          progress={downloadProgress.progress}
          totalTracks={downloadProgress.totalTracks}
        />
      </LPressable>
    );
  }
  return (
    <PremiumButton
      // Removing an existing download never requires access; only acquiring
      // new bytes is gated. The server-synced isPremium keeps the button
      // usable when the local lease chain failed (the lease's real job is
      // offline playback enforcement, not button styling); the server still
      // authorizes every download.
      allowed={
        downloadStatus !== "not_downloaded" || hasOfflineAccess || isPremium
      }
      onDenied={isOnline ? undefined : promptForOfflineAccess}
      premiumComponent={
        <LIcon name="download_premium" size={ICON_SIZE} opacity={0.5} />
      }
      sentryEventName="download_collection"
      hitSlop={12}
    >
      <LPressable
        sentryEventName="download_collection"
        accessibilityLabel={
          downloadStatus === "downloaded"
            ? `Remove downloaded ${type}`
            : `Download ${type}`
        }
        accessibilityRole="button"
        testID={`collection-download-button-${id}`}
        onPress={() => {
          if (downloadStatus !== "not_downloaded") {
            deleteCollection();
          } else {
            downloadCollection().catch(console.error);
          }
        }}
        hitSlop={12}
      >
        <LIcon
          name={downloadStatus === "downloaded" ? "downloaded" : "download"}
          size={ICON_SIZE}
        />
      </LPressable>
    </PremiumButton>
  );
};

export default memo(DownloadButton);
